import { useEffect, useState } from 'react'
import {
  ArrowRight,
  CalendarClock,
  Clock3,
  DollarSign,
  FastForward,
  Home,
  TriangleAlert,
} from 'lucide-react'
import { cn } from '@/lib/utils/helpers'
import { ResidentDialogShell } from './ResidentDialogShell'

export type ReimbursementProcessChoice = 'EVICTION' | 'SKIPPED' | 'NOT_NOW'

const REIMBURSEMENT_TYPE_OPTIONS: Array<{
  value: ReimbursementProcessChoice
  title: string
  description: string
  icon: typeof Home
}> = [
  {
    value: 'EVICTION',
    title: 'Eviction',
    description: 'Nonpayment of rent and the resident is still in the unit.',
    icon: Home,
  },
  {
    value: 'SKIPPED',
    title: 'Skipped',
    description: 'Nonpayment of rent and the resident is out of the unit.',
    icon: FastForward,
  },
  {
    value: 'NOT_NOW',
    title: 'Not Now',
    description: 'Update rent paid status as "Not Paid In Full".',
    icon: Clock3,
  },
]

export interface StartReimbursementProcessDialogProps {
  isOpen: boolean
  residentName: string
  applicantId: string | number
  onClose: () => void
  onSelect: (choice: ReimbursementProcessChoice) => void
}

/**
 * Bulk "Mark Rent Not Paid" reimbursement-type prompt.
 * Select Eviction / Skipped / Not Now, then Cancel or Continue in the footer.
 */
export function StartReimbursementProcessDialog({
  isOpen,
  residentName,
  applicantId,
  onClose,
  onSelect,
}: StartReimbursementProcessDialogProps) {
  const [selected, setSelected] = useState<ReimbursementProcessChoice>('EVICTION')

  useEffect(() => {
    if (!isOpen) return
    // eslint-disable-next-line react-hooks/set-state-in-effect -- reset selection when the dialog opens
    setSelected('EVICTION')
  }, [isOpen])

  if (!isOpen) return null

  return (
    <ResidentDialogShell
      isOpen={isOpen}
      onClose={onClose}
      title="Start Reimbursement Process"
      headerVariant="iconTitle"
      titleIcon={<DollarSign className="h-4 w-4" />}
      subtitle={`${residentName} • Applicant ${applicantId}`}
      maxWidth="max-w-3xl"
      footerAlign="end"
      overlayDataTestId="start-reimbursement-process-overlay"
      panelDataTestId="start-reimbursement-process-panel"
      footer={
        <div
          className="flex items-center justify-end gap-3"
          data-testid="start-reimbursement-process-footer"
        >
          <button
            type="button"
            onClick={onClose}
            data-testid="start-reimbursement-process-cancel-button"
            className="rounded-lg border border-[#1597ff] px-5 py-2 text-sm font-semibold text-[#1597ff] transition hover:bg-[#eaf4ff]"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onSelect(selected)}
            data-testid="start-reimbursement-process-continue-button"
            className="inline-flex items-center gap-1.5 rounded-lg bg-[#1597ff] px-5 py-2 text-sm font-semibold text-white shadow-2xs transition hover:bg-[#0d7acc]"
          >
            Continue
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      }
    >
      <div className="space-y-5" data-testid="start-reimbursement-process-body">
        <div className="flex items-start gap-3 rounded-xl border border-sky-100 bg-sky-50 p-4">
          <CalendarClock className="mt-0.5 h-5 w-5 shrink-0 text-sky-600" aria-hidden="true" />
          <div className="min-w-0 space-y-2">
            <span className="text-sm font-semibold text-slate-900">Time Sensitive Requirements</span>
            <ul className="m-0 list-disc space-y-1.5 pl-4 text-sm leading-relaxed text-slate-600">
              <li>
                Reimbursement requests must be opened within the same first month that rent was not
                paid
              </li>
              <li>
                Legal must be filed within the same first month that rent was not paid (30-day NTV
                only exception)
              </li>
            </ul>
          </div>
        </div>

        <div
          className="flex items-center gap-2.5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3"
          data-testid="start-reimbursement-process-warning"
        >
          <TriangleAlert className="h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" />
          <p className="m-0 text-sm font-semibold text-amber-800">
            Reimbursements will be reduced if timelines are not met
          </p>
        </div>

        <div>
          <h4 className="m-0 text-sm font-semibold text-slate-900">Choose reimbursement type</h4>
          <p className="mt-0.5 text-sm text-slate-500">
            Select the path that applies to this resident.
          </p>

          <div
            className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3"
            role="radiogroup"
            aria-label="Reimbursement type"
          >
            {REIMBURSEMENT_TYPE_OPTIONS.map((option) => {
              const Icon = option.icon
              const isSelected = selected === option.value
              return (
                <button
                  key={option.value}
                  type="button"
                  role="radio"
                  aria-checked={isSelected}
                  onClick={() => setSelected(option.value)}
                  data-testid={`start-reimbursement-process-option-${option.value.toLowerCase()}`}
                  className={cn(
                    'relative flex items-start gap-3 rounded-xl border p-4 text-left transition',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(21_151_255)]/40',
                    isSelected
                      ? 'border-[#1597ff] bg-sky-50 shadow-sm'
                      : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50',
                  )}
                >
                  <span
                    className={cn(
                      'mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg',
                      isSelected ? 'bg-sky-100 text-[#1597ff]' : 'bg-slate-100 text-slate-500',
                    )}
                    aria-hidden="true"
                  >
                    <Icon className="h-4 w-4" />
                  </span>
                  <span className="min-w-0 flex-1 pr-6">
                    <span className="block text-sm font-semibold text-slate-900">{option.title}</span>
                    <span className="mt-0.5 block text-xs leading-snug text-slate-600">
                      {option.description}
                    </span>
                  </span>
                  <span
                    className={cn(
                      'absolute right-3 top-3 inline-flex h-4 w-4 items-center justify-center rounded-full border',
                      isSelected
                        ? 'border-[#1597ff] bg-[#1597ff]'
                        : 'border-slate-300 bg-white',
                    )}
                    aria-hidden="true"
                  >
                    {isSelected ? (
                      <span className="h-1.5 w-1.5 rounded-full bg-white" />
                    ) : null}
                  </span>
                </button>
              )
            })}
          </div>
        </div>
      </div>
    </ResidentDialogShell>
  )
}
